<script setup lang="ts">
/**
 * Partner-portal top header. White background, FZ:Partner wordmark,
 * horizontal nav with a 2px bottom rule on the active tab, partner
 * identity block on the right.
 *
 * The user button opens a dropdown menu (Account / Modifica IBAN /
 * Cambia email / divider / Esci). Consumers subscribe to
 * `select-menu-item`; the legacy bare-`user-click` flow was dropped
 * in Phase 3 once portal-fe wired the menu.
 *
 * Mirrors the design bundle's `PortalShell.jsx::PortalHeader`.
 * Menu styling lives in `src/design-system/shell.css`
 * (`.pp-header__menu` family).
 */
import { onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue'

export interface PortalHeaderTab {
  id: string
  label: string
}

export type PortalMenuItem = 'main' | 'iban' | 'email' | 'logout'

defineProps<{
  tabs: PortalHeaderTab[]
  activeId?: string
  partnerName?: string
  partnerSubtitle?: string
  partnerEmail?: string
  avatarInitials?: string
}>()

const emit = defineEmits<{
  selectTab: [id: string]
  selectMenuItem: [item: PortalMenuItem]
}>()

const menuOpen = ref(false)
const userEl = useTemplateRef<HTMLDivElement>('userEl')

function pickTab(tab: PortalHeaderTab): void {
  emit('selectTab', tab.id)
}

function toggleMenu(): void {
  menuOpen.value = !menuOpen.value
}

function pickMenuItem(item: PortalMenuItem): void {
  menuOpen.value = false
  emit('selectMenuItem', item)
}

function onDocumentMousedown(e: MouseEvent): void {
  if (!menuOpen.value) return
  const root = userEl.value
  if (root && !root.contains(e.target as Node)) {
    menuOpen.value = false
  }
}

function onDocumentKey(e: KeyboardEvent): void {
  if (!menuOpen.value) return
  if (e.key === 'Escape') menuOpen.value = false
}

onMounted(() => {
  document.addEventListener('mousedown', onDocumentMousedown)
  document.addEventListener('keydown', onDocumentKey)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocumentMousedown)
  document.removeEventListener('keydown', onDocumentKey)
})
</script>

<template>
  <header class="fz-portal-header">
    <a class="fz-portal-header__brand" href="#">
      FZ<span class="fz-portal-header__brand-sep">:</span><strong>Partner</strong>
    </a>
    <nav class="fz-portal-header__nav" aria-label="Sezioni del portale">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        type="button"
        :class="['fz-portal-header__nav-item', { 'is-active': activeId === tab.id }]"
        @click="pickTab(tab)"
      >
        {{ tab.label }}
      </button>
    </nav>
    <span class="fz-portal-header__spacer" />
    <div ref="userEl" class="fz-portal-header__user pp-header__user">
      <button
        type="button"
        class="fz-portal-header__user-btn"
        :aria-haspopup="true"
        :aria-expanded="menuOpen"
        :aria-label="partnerName || 'Profilo'"
        @click="toggleMenu"
      >
        <span class="fz-portal-header__user-text">
          <span v-if="partnerName" class="fz-portal-header__name">{{ partnerName }}</span>
          <span v-if="partnerSubtitle" class="fz-portal-header__role">{{ partnerSubtitle }}</span>
        </span>
        <span v-if="avatarInitials" class="fz-portal-header__avatar">{{ avatarInitials }}</span>
      </button>
      <div v-if="menuOpen" class="pp-header__menu" role="menu">
        <div class="pp-header__menu-head">
          <div class="pp-header__menu-name">{{ partnerName ?? '' }}</div>
          <div class="pp-header__menu-sub mono">{{ partnerEmail ?? '' }}</div>
        </div>
        <button
          type="button"
          class="pp-header__menu-item"
          role="menuitem"
          @click="pickMenuItem('main')"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <circle cx="12" cy="8" r="4" />
            <path d="M4 21v-1a8 8 0 0 1 16 0v1" />
          </svg>
          Account
        </button>
        <button
          type="button"
          class="pp-header__menu-item"
          role="menuitem"
          @click="pickMenuItem('iban')"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <rect x="3" y="6" width="18" height="13" rx="0" />
            <line x1="3" y1="11" x2="21" y2="11" />
          </svg>
          Modifica IBAN
        </button>
        <button
          type="button"
          class="pp-header__menu-item"
          role="menuitem"
          @click="pickMenuItem('email')"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <rect x="3" y="5" width="18" height="14" rx="0" />
            <polyline points="3 7 12 13 21 7" />
          </svg>
          Cambia email
        </button>
        <div class="pp-header__menu-sep" />
        <button
          type="button"
          class="pp-header__menu-item"
          role="menuitem"
          @click="pickMenuItem('logout')"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          Esci
        </button>
      </div>
    </div>
  </header>
</template>

<style scoped>
.fz-portal-header {
  display: flex;
  align-items: center;
  background: var(--bg);
  border-bottom: 1px solid var(--border-subtle-00);
  height: 64px;
  padding: 0 var(--spacing-07);
}
.fz-portal-header__brand {
  font-size: var(--fs-18);
  font-weight: var(--fw-light);
  letter-spacing: -0.2px;
  color: var(--text-primary);
  text-decoration: none;
  margin-right: var(--spacing-09);
}
.fz-portal-header__brand strong {
  font-weight: var(--fw-semibold);
}
.fz-portal-header__brand-sep {
  color: var(--blue-60);
}
.fz-portal-header__nav {
  display: flex;
  height: 100%;
  gap: var(--spacing-02);
}
.fz-portal-header__nav-item {
  display: flex;
  align-items: center;
  padding: 0 var(--spacing-05);
  color: var(--text-secondary);
  text-decoration: none;
  font-size: var(--fs-14);
  border-bottom: 2px solid transparent;
  cursor: pointer;
  background: none;
  border-top: none;
  border-left: none;
  border-right: none;
  font-family: inherit;
}
.fz-portal-header__nav-item:hover {
  color: var(--text-primary);
}
.fz-portal-header__nav-item:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: -2px;
}
.fz-portal-header__nav-item.is-active {
  color: var(--text-primary);
  border-bottom-color: var(--blue-60);
  font-weight: var(--fw-semibold);
}
.fz-portal-header__spacer {
  flex: 1;
}
/* Wrapper div hosts the menu (position: relative comes from .pp-header__user
 * via shell.css). The button receives focus styles. */
.fz-portal-header__user {
  display: flex;
  align-items: center;
}
.fz-portal-header__user-btn {
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  color: inherit;
}
.fz-portal-header__user-btn:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}
.fz-portal-header__user-text {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
}
.fz-portal-header__name {
  font-size: var(--fs-14);
  color: var(--text-primary);
}
.fz-portal-header__role {
  font-size: var(--fs-12);
  color: var(--text-secondary);
}
.fz-portal-header__avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--gray-20);
  color: var(--text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--fs-12);
  font-weight: var(--fw-semibold);
}

/* Position the dropdown menu relative to the user wrapper. The
 * appearance rules come from .pp-header__menu in shell.css. */
.pp-header__user {
  position: relative;
}
</style>
